/**
 * MokaLinkDialog.jsx — Moka ödeme linki üretimi + yönetimi.
 *
 * AdminAccounting.jsx'in Taksitler sekmesindeki bir satırdan (`installment`)
 * açılır. Overlay/kart görsel deseni CollectModal/NoteCollectModal (aynı
 * dosya) ile BİREBİR AYNI — shadcn Dialog değil, `fixed inset-0` + `card-clay`.
 *
 * AKIŞ:
 *   1. Açılışta `installment_id` için AKTİF link var mı diye önceden sorgulanır
 *      (proaktif kontrol — race'i tamamen ELEMEZ, bkz. madde 5).
 *   2. Aktif link VARSA: link özeti + gönderim durumu + yeniden gönder/iptal
 *      + "Yeni link üret (eskisini geçersiz kıl)" gösterilir (force=true akışı,
 *      ikinci bir açık onay ister — para/iletişim kararı).
 *   3. Aktif link YOKSA (ya da "yeni üret" seçildiyse): görüşme notu
 *      (opsiyonel) + POS hesabı seçimi formu gösterilir. Sesli onay
 *      checkbox'ı 28 Eylül 2026'dan itibaren KALDIRILDI (zorunluluk backend'de
 *      de kalktı) — not alanı geriye dönük uyumluluk için kaldı.
 *   4. 502 `moka_ip_izinli_degil` toast'a DEĞİL, ayrı bir bilgi bandına yazılır
 *      (görev talebi: "kimlik hatası gibi gösterme").
 *   5. Yarış durumu: proaktif kontrole rağmen başka bir admin araya girip link
 *      ürettiyse, POST 409 `moka_link_zaten_aktif` döner ve artık
 *      `detail.existing_link` (link_id, status, amount, created_at, created_by,
 *      payment_page_url, delivery) taşır. Bu durumda toast yerine dialog içinde
 *      bir özet paneli gösterilir; "yeni link üret (force)" aynı ConfirmDialog
 *      akışına bağlanır. `existing_link` gelmezse (geriye uyumluluk) eski toast
 *      davranışına düşülür.
 */
import { useState } from 'react';
import { Link2, X, Copy, Check, RefreshCw, Info, ShieldAlert, Ban } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '@/utils/apiError';
import { fmtTRY } from '@/utils/formatCurrency';
import { ConfirmDialog, StatusBadge } from '@/components/admin';
import {
  useMokaLinks,
  useCreateMokaLink,
  useResendMokaLink,
  useCancelMokaLink,
} from '@/api/mutations/moka';

function fmtDateTime(iso, lang) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function deliveryVariant(status) {
  if (status === 'sent') return 'success';
  if (status === 'error') return 'danger';
  return 'neutral';
}

function deliveryLabel(status, t) {
  if (status === 'sent') return t('admin.moka_delivery_status_sent');
  if (status === 'error') return t('admin.moka_delivery_status_error');
  if (status === 'skipped') return t('admin.moka_delivery_status_skipped');
  return t('admin.moka_delivery_status_not_sent');
}

function CopyButton({ text, t }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
      aria-label={t('admin.moka_copy')}
      data-testid="moka-link-copy-btn"
      className="p-1.5 rounded-lg hover:bg-gray-100 text-[var(--text-muted)] inline-flex items-center shrink-0"
    >
      {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
    </button>
  );
}

export default function MokaLinkDialog({ installment, banks, onClose, t }) {
  const { lang } = useLanguage();
  const toast = useToast();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [voiceNote, setVoiceNote] = useState('');
  const [bankId, setBankId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  const [ipBlockedMessage, setIpBlockedMessage] = useState(null);
  const [conflictLink, setConflictLink] = useState(null);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const posBanks = (banks || []).filter(
    (b) => (b.account_type || 'bank') === 'pos' && b.is_active !== false,
  );

  const installmentId = installment?.installment_id;
  const { data: linksData, isLoading: loadingActive, refetch: refetchActive } = useMokaLinks(
    { installmentId, status: 'active', limit: 1 },
    { enabled: !!installmentId && !justCreated },
  );

  const createLink = useCreateMokaLink();
  const resendLink = useResendMokaLink();
  const cancelLink = useCancelMokaLink();

  if (!installment) return null;

  const activeLink = justCreated || linksData?.links?.[0] || null;
  const displayAmount = Number(installment.amount_with_interest || installment.amount || 0);
  const bankMissing = !bankId;

  const resetCreateForm = () => {
    setVoiceNote('');
    setBankId('');
    setSubmitted(false);
    setIpBlockedMessage(null);
    setConflictLink(null);
  };

  const submitCreate = async (force) => {
    setSubmitted(true);
    setIpBlockedMessage(null);
    setConflictLink(null);
    if (bankMissing) return;
    try {
      const result = await createLink.mutateAsync({
        installment_id: installmentId,
        bank_id: bankId,
        voice_approval_note: voiceNote || undefined,
        force: !!force,
      });
      setJustCreated(result);
      setShowCreateForm(false);
      setForceConfirmOpen(false);
      toast.success(t('admin.moka_link_created_banner'));
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === 'moka_ip_izinli_degil') {
        setIpBlockedMessage(getApiErrorMessage(err, lang));
        setForceConfirmOpen(false);
        return;
      }
      if (err?.response?.status === 409) {
        // Proaktif kontrole rağmen yarış: başka bir admin az önce ürettiyse.
        const existing = err?.response?.data?.detail?.existing_link;
        setForceConfirmOpen(false);
        if (existing) {
          setConflictLink(existing);
          return;
        }
        // Geriye uyumluluk: existing_link gelmediyse eski toast davranışı.
        toast.error(getApiErrorMessage(err, lang));
        refetchActive();
        return;
      }
      toast.error(getApiErrorMessage(err, lang));
      setForceConfirmOpen(false);
    }
  };

  const handleSubmit = () => {
    if (activeLink && showCreateForm) {
      // Aktif link varken yeni üretim — force=true ikinci onay ister.
      setSubmitted(true);
      if (bankMissing) return;
      setForceConfirmOpen(true);
      return;
    }
    submitCreate(false);
  };

  const handleResend = async () => {
    try {
      const result = await resendLink.mutateAsync(activeLink.link_id);
      if (justCreated) setJustCreated(result);
      else refetchActive();
      toast.success(t('admin.moka_resend_success'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleCancel = async () => {
    try {
      await cancelLink.mutateAsync(activeLink.link_id);
      toast.success(t('admin.moka_cancel_success'));
      setCancelConfirmOpen(false);
      setJustCreated(null);
      refetchActive();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setCancelConfirmOpen(false);
    }
  };

  const showForm = (!activeLink || showCreateForm) && !conflictLink;
  const saving = createLink.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card-clay p-5 max-w-md w-full max-h-[90vh] overflow-y-auto" data-testid="moka-link-dialog">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-[var(--brand-primary)]" />
            <h3 className="font-bold">{t('admin.moka_link_dialog_title')}</h3>
          </div>
          <button onClick={onClose} aria-label={t('admin.btn_close')} className="text-[var(--text-muted)]">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-[var(--text-muted)] mb-3">
          {installment.user_name} — {t('admin.acc_col_installment_no')} {installment.installment_no}/{installment.total_installments} — {fmtTRY(displayAmount)}
        </p>

        {ipBlockedMessage && (
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-2 mb-3 text-xs text-sky-800 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>{ipBlockedMessage}</span>
          </div>
        )}

        {loadingActive && !justCreated && (
          <p className="text-sm text-[var(--text-muted)] py-4 text-center">{t('admin.moka_loading')}</p>
        )}

        {/* ── Aktif / yeni üretilmiş link paneli ─────────────────────────────── */}
        {!loadingActive && activeLink && !showCreateForm && (
          <div className="space-y-3 mb-4" data-testid="moka-active-link-panel">
            {justCreated && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2 text-xs text-emerald-700 flex items-center gap-2">
                <Check size={14} />
                {t('admin.moka_link_created_banner')}
              </div>
            )}
            <div className="flex items-center gap-2">
              <StatusBadge variant="success" label={t('admin.moka_active_link_title')} dot />
              <span className="text-xs text-[var(--text-muted)]">{fmtTRY(activeLink.amount)}</span>
            </div>
            <div>
              <label className="text-xs font-bold block mb-0.5">{t('admin.moka_link_url_label')}</label>
              <div className="flex items-center gap-1 px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50">
                <span className="text-xs font-mono truncate flex-1" data-testid="moka-link-url">
                  {activeLink.payment_page_url}
                </span>
                <CopyButton text={activeLink.payment_page_url} t={t} />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold block mb-0.5">{t('admin.moka_delivery_title')}</label>
              <div className="flex gap-2 flex-wrap">
                <StatusBadge variant={deliveryVariant(activeLink.delivery?.sms?.status)} label={`${t('admin.moka_channel_sms')}: ${deliveryLabel(activeLink.delivery?.sms?.status, t)}`} />
                <StatusBadge variant={deliveryVariant(activeLink.delivery?.email?.status)} label={`${t('admin.moka_channel_email')}: ${deliveryLabel(activeLink.delivery?.email?.status, t)}`} />
              </div>
            </div>
            {activeLink.voice_approval?.confirmed && (
              <p className="text-[11px] text-[var(--text-muted)]">
                {t('admin.moka_voice_approval_by')}: {activeLink.voice_approval.admin_name} — {fmtDateTime(activeLink.voice_approval.confirmed_at, lang)}
                {activeLink.voice_approval.note ? ` — “${activeLink.voice_approval.note}”` : ''}
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleResend}
                disabled={resendLink.isPending}
                data-testid="moka-resend-btn"
                className="px-3 py-2 rounded-xl bg-sky-50 text-sky-700 text-xs font-bold hover:bg-sky-100 disabled:opacity-50 min-h-[44px] inline-flex items-center gap-1.5"
              >
                <RefreshCw size={13} />
                {resendLink.isPending ? t('admin.moka_resending') : t('admin.moka_resend')}
              </button>
              <button
                onClick={() => setCancelConfirmOpen(true)}
                disabled={cancelLink.isPending}
                data-testid="moka-cancel-link-btn"
                className="px-3 py-2 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold hover:bg-rose-100 disabled:opacity-50 min-h-[44px] inline-flex items-center gap-1.5"
              >
                <Ban size={13} />
                {t('admin.moka_cancel_link')}
              </button>
            </div>
            <button
              onClick={() => { resetCreateForm(); setShowCreateForm(true); }}
              data-testid="moka-generate-new-btn"
              className="text-xs font-bold text-[var(--brand-primary)] hover:underline"
            >
              {t('admin.moka_generate_new_link')}
            </button>
          </div>
        )}

        {/* ── Yarış çakışması: 409 + existing_link özeti ──────────────────────── */}
        {conflictLink && (
          <div className="space-y-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="moka-conflict-panel">
            <div className="flex items-start gap-2 text-xs font-bold text-amber-800">
              <ShieldAlert size={14} className="mt-0.5 shrink-0" />
              {t('admin.moka_conflict_title')}
            </div>
            <p className="text-xs text-amber-700">{t('admin.moka_conflict_desc')}</p>
            <div className="flex items-center gap-2">
              <StatusBadge variant="success" label={t('admin.moka_active_link_title')} dot />
              <span className="text-xs text-[var(--text-muted)]">{fmtTRY(conflictLink.amount)}</span>
            </div>
            <div>
              <label className="text-xs font-bold block mb-0.5">{t('admin.moka_link_url_label')}</label>
              <div className="flex items-center gap-1 px-2 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50">
                <span className="text-xs font-mono truncate flex-1" data-testid="moka-conflict-link-url">
                  {conflictLink.payment_page_url}
                </span>
                <CopyButton text={conflictLink.payment_page_url} t={t} />
              </div>
            </div>
            {conflictLink.delivery && (
              <div className="flex gap-2 flex-wrap">
                <StatusBadge variant={deliveryVariant(conflictLink.delivery?.sms?.status)} label={`${t('admin.moka_channel_sms')}: ${deliveryLabel(conflictLink.delivery?.sms?.status, t)}`} />
                <StatusBadge variant={deliveryVariant(conflictLink.delivery?.email?.status)} label={`${t('admin.moka_channel_email')}: ${deliveryLabel(conflictLink.delivery?.email?.status, t)}`} />
              </div>
            )}
            <p className="text-[11px] text-[var(--text-muted)]">
              {t('admin.moka_conflict_created_by')}: {conflictLink.created_by || '—'} — {fmtDateTime(conflictLink.created_at, lang)}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => { setConflictLink(null); setForceConfirmOpen(true); }}
                data-testid="moka-conflict-force-btn"
                className="px-3 py-2 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold hover:bg-rose-100 min-h-[44px]"
              >
                {t('admin.moka_generate_new_link')}
              </button>
              <button
                onClick={() => { setConflictLink(null); refetchActive(); }}
                data-testid="moka-conflict-dismiss-btn"
                className="px-3 py-2 rounded-xl text-xs font-bold text-[var(--text-muted)] hover:underline min-h-[44px]"
              >
                {t('admin.moka_back_to_active')}
              </button>
            </div>
          </div>
        )}

        {/* ── Yeni link üretim formu ──────────────────────────────────────────── */}
        {!loadingActive && showForm && (
          <div className="space-y-3 mb-4" data-testid="moka-create-form">
            {activeLink && showCreateForm && (
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-xs font-bold text-[var(--text-muted)] hover:underline"
              >
                ← {t('admin.moka_back_to_active')}
              </button>
            )}
            {posBanks.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 text-xs text-amber-700 flex items-start gap-2">
                <Info size={14} className="mt-0.5 shrink-0" />
                {t('admin.moka_no_pos_banks')}
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="moka-voice-note" className="text-xs font-bold block mb-0.5">{t('admin.moka_voice_note_label')}</label>
                  <textarea
                    id="moka-voice-note"
                    value={voiceNote}
                    onChange={(e) => setVoiceNote(e.target.value)}
                    placeholder={t('admin.moka_voice_note_placeholder')}
                    rows={2}
                    data-testid="moka-voice-note-input"
                    className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="moka-bank-select" className="text-xs font-bold block mb-0.5">
                    {t('admin.moka_bank_label')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="moka-bank-select"
                    value={bankId}
                    onChange={(e) => setBankId(e.target.value)}
                    data-testid="moka-bank-select"
                    className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
                  >
                    <option value="">{t('admin.moka_bank_placeholder')}</option>
                    {posBanks.map((b) => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
                  </select>
                  {submitted && bankMissing && (
                    <p role="alert" className="text-[11px] font-bold text-rose-600 mt-0.5">
                      {t('admin.moka_bank_required')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    data-testid="moka-generate-link-btn"
                    className="btn-success px-6 py-2 text-sm flex-1 disabled:opacity-50 min-h-[44px]"
                  >
                    {saving ? t('admin.moka_generating') : t('admin.moka_generate_link')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-muted)] min-h-[44px]">
            {t('admin.btn_close')}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={forceConfirmOpen}
        onOpenChange={(o) => { if (!o) setForceConfirmOpen(false); }}
        title={t('admin.moka_force_confirm_title')}
        description={t('admin.moka_force_confirm_desc')}
        variant="warning"
        confirmLabel={t('admin.moka_generate_new_link')}
        onConfirm={() => submitCreate(true)}
        loading={saving}
      />
      <ConfirmDialog
        open={cancelConfirmOpen}
        onOpenChange={(o) => { if (!o) setCancelConfirmOpen(false); }}
        title={t('admin.moka_cancel_confirm_title')}
        description={t('admin.moka_cancel_confirm_desc')}
        variant="danger"
        confirmLabel={t('admin.moka_cancel_link')}
        onConfirm={handleCancel}
        loading={cancelLink.isPending}
      />
    </div>
  );
}
